import { centsToUnits } from '@systemeio/ui'
import { pricePlanTypeLocoKeys } from 'modules/digital-product/price-plans/constants/price-plan-type-loco-keys'
import { PricePlanType, PricePlanTypeEnum } from '@systemeio/types'
import { taxBehaviorLocoKeys } from 'modules/payment/tax-behavior'
import React from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { currencyFormat } from 'shared/utils/currency-format'

interface PricePlanDescriptionProps {
  pricePlan: PricePlanType
}

function PricePlanDescription({ pricePlan }: PricePlanDescriptionProps) {
  const { t } = useLocoTranslation()
  const { user } = useUser()
  return (
    <div className="font-inter border-text-secondary/30 flex flex-1 flex-col items-start gap-2 rounded-lg border px-5 py-3">
      <p>
        <span className="text-text-primary font-bold">
          {t('dashboard.funnel.configuration.common.price_plan.name_label')}:{' '}
        </span>
        {pricePlan.name}
      </p>
      <p>
        <span className="text-text-primary font-bold">
          {t('dashboard.funnel.configuration.common.price_plan.statement_descriptor')}:{' '}
        </span>
        {pricePlan.statementDescriptor}
      </p>
      <p>
        <span className="text-text-primary font-bold">
          {t('dashboard.funnel.configuration.common.price_plan.type')}:{' '}
        </span>
        {t(pricePlanTypeLocoKeys[pricePlan.type])}
      </p>
      {pricePlan.type === PricePlanTypeEnum.limited_subscription && (
        <p>
          <span className="text-text-primary font-bold">
            {t(
              'dashboard.funnel.configuration.common.price_plan.subscription_plan.limit_of_payments',
            )}
            :{' '}
          </span>
          {pricePlan.recurringOptions.limitOfPayments}
        </p>
      )}
      {pricePlan.recurringOptions &&
        pricePlan.recurringOptions.trialPeriod > 0 &&
        pricePlan.recurringOptions.trialInterval && (
          <p>
            <span className="text-text-primary font-bold">
              {`${t(
                'dashboard.funnel.configuration.common.price_plan.subscription_plan.trial_period',
              )} : `}
            </span>
            {t(
              `dashboard.funnel.configuration.common.price_plan.period.${pricePlan.recurringOptions.trialInterval}`,
              {
                count: pricePlan.recurringOptions.trialPeriod,
              },
            )}
          </p>
        )}
      {pricePlan.type === PricePlanTypeEnum.one_shot ? (
        <p>
          <span className="text-text-primary font-bold">
            {t('dashboard.funnel.configuration.common.price_plan.direct_charge_amount')}:{' '}
          </span>
          {`${currencyFormat(
            centsToUnits(pricePlan.amount),
            pricePlan.currency,
            user?.dashboardLocale,
          )}`}
        </p>
      ) : (
        <p>
          <span className="text-text-primary font-bold">
            {t('dashboard.funnel.configuration.common.price_plan.direct_charge_amount')}:{' '}
          </span>
          {pricePlan.amount &&
            `${currencyFormat(
              centsToUnits(pricePlan.amount),
              pricePlan.currency,
              user?.dashboardLocale,
            )} ${t(
              'dashboard.funnel.configuration.common.price_plan.subscription_plan.interval_label',
            )} ${t(
              `dashboard.funnel.configuration.common.price_plan.period.${pricePlan.recurringOptions.interval}`,
              {
                count: pricePlan.recurringOptions.intervalCount,
              },
            )}`}
        </p>
      )}
      <p>
        <span className="text-text-primary font-bold">
          {t('dashboard.payment.tax_behavior.title')}:{' '}
        </span>
        {t(taxBehaviorLocoKeys[pricePlan.taxBehavior])}
      </p>
    </div>
  )
}

export default PricePlanDescription
